/**
 * Outlook sending-identity predicates.
 *
 * Since VALE-1293 a send leaves from the mailbox of the person who clicks Send;
 * the matter's acting solicitor is only the fallback for a send nobody clicked.
 * So "does this send land in my own Sent Items?" is not a question the frontend
 * can answer from the matter's acting solicitor, nor from comparing addresses
 * (a solicitor's Vale login and their connected mailbox are often on different
 * domains). The backend answers it from the same resolution the send uses, as
 * `OutlookSendIdentity.sendsFromViewer`, and every send surface reads that.
 */

import type { OutlookSendIdentity } from '@/lib/services/communications/types';

/**
 * Case-insensitive email comparison. Graph, Keycloak, and our own records
 * disagree on casing for the same mailbox, so never compare raw.
 *
 * Not for deciding whether a send leaves from the viewer's own mailbox: use
 * `sendsFromViewer`, which is decided by user id.
 */
export function emailsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * True when the send leaves from the viewer's own mailbox, so it lands in
 * their own Sent Items and the "Send me a copy" control is redundant. The
 * backend drops a requested copy on exactly this answer (VALE-1873).
 *
 * False (keep the control) whenever the identity is absent (SES, which covers a
 * lawyer whose mailbox has never connected), in shared-mailbox mode, and when
 * blocked.
 */
export function sendsFromViewer(
  identity: OutlookSendIdentity | null | undefined
): boolean {
  return identity?.sendsFromViewer === true;
}

/**
 * The "Send me a copy" control's label, on every send surface. The copy is a
 * BCC (VALE-1873), and the label says so: a CC would show the recipient the
 * solicitor copying themselves.
 */
export const SENDER_COPY_LABEL = 'Bcc me a copy';
